"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import HotelPrice from "@/components/hotel/hotels-result/Results/Content/HotelPrice"
import CheckedIcon from "@/components/icons/CheckedIcon"
import DoubleBedIcon from "@/components/icons/DoubleBedIcon"
import FareIcon from "@/components/icons/FareIcon"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useRoomResource } from "@/hooks/hotel/useRoomResource"
import { HotelRoom } from "@/lib/redux/features/hotel/type"
import { handleChooseHotel } from "@/utils/hotel/handleChooseHotel"
import { useHotelMarkupPolicy } from "@/hooks/hotel/useHotelMarkupPolicy"
import { faUtensils } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, Row, Tag, theme } from "antd"
import dayjs from "dayjs"
import { FC, useMemo } from "react"
import PromotionIconV2 from "@/components/icons/PromotionIconV2"
import { useHotelCancellationPolicies } from "@/hooks/hotel/useHotelCancellationPolicies"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import _ from "lodash"

type RoomOptionProps = {
  option: HotelRoom,
  showTitle?: boolean,
  showPrice?: boolean,
  cheapest?: boolean,
  index: number,
  openLoginForm?: () => void
}

const RoomOption: FC<RoomOptionProps> = ({ showTitle = true, showPrice = true, cheapest = false, option, index, openLoginForm }) => {
  const { getTranslatedText, getFormatCurrency, getValidDayjs } = useTETranslation()
  const { t } = useTranslation()
  const token = theme.useToken().token
  const router = useTERouter()
  const resource = useRoomResource(option)
  const isDesktop = useIsDesktop()
  const { getPerNightPrice } = useHotelMarkupPolicy()
  const optionPrice = getPerNightPrice(option)

  const bedTypes = useMemo(() => {
    const bedType = resource?.bedType || t("booking.1_double_bed")
    const _bedTypes = bedType.split(';')
    return _bedTypes.length <= 1 ? [bedType] : _bedTypes.map((b: string) => {
      const [name, type] = b.split(': :')
      if (!name || !type) {
        return [name, type].filter(v => !!v).join(': :')
      }
      return `- ${name} ${t("common.has")} ${type}`
    })
  }, [])

  const renderItem = ({ text, icon, danger, success, warning }: any) => {
    return (
      <Row
        gutter={[5, 5]}
        wrap={false}
        className={`hotel__rooms--option-item${danger ? " hotel__rooms--option-item--danger" : ""}${success ? " hotel__rooms--option-item--success" : ""}${warning ? " hotel__rooms--option-item--warning" : ""}`}
      >
        <Col flex={"2rem"}>
          <Text>
            {icon}
          </Text>
        </Col>
        <Col>
          <Text>
            {text}
          </Text>
        </Col>
      </Row>
    )
  }

  const cancellationPolicy = useHotelCancellationPolicies(option);

  const parseCancellationPolicy = ({ before, rate, fee }: any) => {
    let dayjsBefore = getValidDayjs(before)
    if (dayjsBefore.format("HH:mm") === '00:00') {
      dayjsBefore = dayjsBefore.subtract(1, 'minute')
    }
    return {
      text: fee <= 0
        ? `${t("hotel.free_cancellation_before")} ${dayjsBefore.format(`HH ${_.lowerFirst(t("common.hour"))} DD MMMM`)}`
        : fee >= optionPrice
          ? t("hotel.no_refunds")
          : `${t("hotel.fee_cancellation", { amount: getFormatCurrency(fee), date: dayjsBefore.format(`HH ${_.lowerFirst(t("common.hour"))} DD MMMM`) })}`,
      icon: <FareIcon color="red" style={{ fontSize: '1.6rem' }} />,
      success: fee <= 0,
      danger: fee == optionPrice,
      warning: fee !== optionPrice && fee > 0
    }
  }

  const specailBenifits = (
    <>
      {showTitle && (
        <Row>
          <Title style={{ marginTop: 0 }} level={4}>{t("common.choice")} {index + 1}</Title>
        </Row>
      )}
      {cheapest && (<Row>
        <Col>
          <Tag
            style={{
              whiteSpace: 'pre-wrap'
            }}
            color='volcano'
            variant='solid'
          >
            {t("hotel.lowest_price")}
          </Tag>
        </Col>
      </Row>)}
      {[
        // free cancellationPolicy
        ...(option?.freeCancellation && getValidDayjs(option.freeCancellationDate).diff(dayjs(), 'hour') >= 1 ? [
          {
            text: `${t("hotel.free_cancellation_before")} ${getValidDayjs(option.freeCancellationDate).subtract(1, 'minute').format(`HH ${_.lowerFirst(t("common.hour"))} DD MMMM`)}`,
            icon: <FareIcon color="red" style={{ fontSize: '1.6rem' }} />,
            success: true
          }
        ] : []),
        ...(option?.freeCancellation
          ? cancellationPolicy.map(parseCancellationPolicy)
          : [
            {
              text: t("hotel.no_refunds"),
              icon: <FareIcon color="red" style={{ fontSize: '1.6rem' }} />,
              danger: true
            },
          ]),

        ...(option.freeBreakfast ? [
          {
            text: t("hotel.includes_breakfirst"),
            icon: <FontAwesomeIcon icon={faUtensils} style={{ fontSize: '1.6rem' }} />,
            success: true
          }
        ] : []),
        ...(option?.payAtHotel ? [
          {
            text: t("hotel.pay_at_hotel"),
            icon: <CheckedIcon style={{ fontSize: '1.6rem' }} />,
            success: true
          }
        ] : []),

        ...(!option.freeBreakfast ? [
          {
            text: t("hotel.without_breakfirst"),
            icon: <CheckedIcon style={{ fontSize: '1.6rem' }} />,
          }
        ] : []),
        // {
        //   text: "Xem phụ thu người lớn/ trẻ em",
        //   icon: <FareIcon style={{ fontSize: '1.6rem' }} />
        // },
        // {
        //   text: "Xác nhận đặt phòng trong 1 phút",
        //   icon: <LightningIcon style={{ fontSize: '1.6rem' }} />,
        //   danger: true
        // },
        ...option.benefits.map((benifit) => {
          return {
            text: getTranslatedText(benifit.translatedBenefitName, benifit.benefitName),
            icon: <CheckedIcon style={{ fontSize: '1.6rem' }} />,
          }
        }),
        // {
        //   text: `An tâm đặt phòng. ${process.env.NEXT_PUBLIC_PLATFORM_NAME || ""} hồ trợ xuất hóa đơn nhanh chóng, tiết kiệm thời gian cho bạn`,
        //   icon: <BillIcon style={{ fontSize: '1.6rem' }} />,
        // },
        ...(option?.promotionDetail ? [
          {
            text: `${option?.promotionDetail?.description}, ${t("booking.save")} ${getFormatCurrency(option?.promotionDetail?.savingAmount || 0)}`,
            icon: <PromotionIconV2 style={{ fontSize: '1.6rem' }} />,
            success: true
          }
        ] : []),
      ].map((option) => {
        return (
          <>
            {renderItem(option)}
          </>
        )
      })}
    </>
  )


  const roomPrice = (
    <>
      <Row justify='end'>
        <HotelPrice price={optionPrice} promotionDetail={option.promotionDetail} />
      </Row>
      <Row justify='end' style={{ marginTop: '0.5rem' }}>
        <Text type='secondary' style={{ textAlign: 'end' }}>
          ({t("hotel.price_includes_fee_tax")})
        </Text>
      </Row>
      {/* <Row justify='end' style={{ marginTop: '0.5rem' }}>

        <Text type='secondary' style={{ textAlign: 'end' }}>
          Giá thành viên <HotelPrice />
        </Text>
      </Row> */}
      <Row justify='end' style={{ marginTop: '0.5rem' }}>
        <Button
          type='primary'
          onClick={() => {
            handleChooseHotel({
              ...option,
              cancellationPoliciesText: cancellationPolicy.map(p => p.text).concat(
                option?.freeCancellation && getValidDayjs(option.freeCancellationDate).diff(dayjs(), 'hour') >= 1
                  ? [`Hủy miễn phí trước ${getValidDayjs(option.freeCancellationDate).subtract(1, 'minute').format("HH:mm DD MMMM")}`]
                  : []
              ),
              cancellationPoliciesTextEn: cancellationPolicy.map(p => p.textEn).concat(
                option?.freeCancellation && getValidDayjs(option.freeCancellationDate).diff(dayjs(), 'hour') >= 1
                  ? [`Free cancellation before ${getValidDayjs(option.freeCancellationDate).subtract(1, 'minute').format("HH:mm DD MMMM")}`]
                  : []
              ),
            } as HotelRoom, (orderCode) => router.push(`/booking/hotel?orderCode=${encodeURIComponent(orderCode)}`))
          }}
        >
          {t("hotel.choose_room")}
        </Button>
      </Row>
      {/* {!isAuthen && (
        <Row justify='end' style={{ marginTop: '0.5rem' }}>
          <Link onClick={() => openLoginForm && openLoginForm()} underline style={{ textAlign: 'end' }}>
            {t("hotel.login_have_good_price")}
          </Link>
        </Row>
      )} */}
    </>
  )

  if (!isDesktop) {
    return (
      <>
        {specailBenifits}
        {showPrice && roomPrice}
      </>
    )
  }

  return (
    <Row wrap={false}>
      <Col flex={1} style={{ paddingInline: '1.2rem' }}>
        {specailBenifits}
      </Col>
      <Col
        style={{ borderLeft: `1px solid ${token.colorBorder}`, borderRight: `1px solid ${token.colorBorder}`, paddingInline: '1rem' }}
        flex={"17rem"}
      >
        <Row justify='center'>
          <DoubleBedIcon style={{ fontSize: '3.2rem' }} />
        </Row>
        {bedTypes.map((bedT: string) => (
          <Row justify='center'>
            <Text style={{ textAlign: 'center' }}>
              {bedT}
            </Text>
          </Row>
        ))}

        {/* {option.extraBeds ? (
          <>
            <Divider style={{ marginBlock: 2 }} />
            <Row justify='center'>
              <Text type={hotel?.childAndExtraBedPolicy?.childrenStayFree ? 'success' : undefined} style={{ textAlign: 'center' }}>
                {option?.extraBeds} bé từ {hotel?.childAndExtraBedPolicy?.childrenAgeFrom} đên {hotel?.childAndExtraBedPolicy?.childrenAgeTo} tuổi{hotel?.childAndExtraBedPolicy?.childrenStayFree ? " được ở miễn phí!" : ""}
              </Text>
            </Row>
          </>
        ) : <></>} */}
      </Col>
      {showPrice && (
        <Col flex={"24rem"}>
          {roomPrice}
        </Col>
      )}
    </Row>
  )
}

export default RoomOption